import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface SectionErrorProps {
  /** One-line headline. Optional: some sections say everything in the body. */
  title?: ReactNode;
  /** Calm supporting copy: what happened and what still holds. */
  body?: ReactNode;
  /** Retry handler. When provided, renders a Retry button. */
  onRetry?: () => void;
  /** Retry button label (already translated). */
  retryLabel?: string;
  /** A refetch is in flight: the button spins and is disabled. */
  retrying?: boolean;
  /**
   * A second way out beside Retry (an already-built button), for when the
   * operator's own change is what failed, e.g. "Undo my changes". Rendered
   * only when given.
   */
  secondaryAction?: ReactNode;
  /**
   * `block` (default): centred column, for a whole tab or list that failed.
   * `compact`: the same block with less padding, for a card or an open row.
   * `inline`: one row (icon, text, Retry on the right), for a banner above
   * content that still renders.
   */
  variant?: 'block' | 'compact' | 'inline';
  className?: string;
  'data-testid'?: string;
}

/**
 * Section-level error block (DESIGN.md section 15.3): a destructive tint on a
 * bordered box, the icon and title carrying the red, calm body copy and one
 * Retry. The sibling of `PageErrorState`, which is for a whole page; use this
 * when one section of a page failed and the rest still renders. A failed read
 * is never shown as an empty state.
 */
export function SectionError({
  title,
  body,
  onRetry,
  retryLabel = 'Retry',
  retrying = false,
  secondaryAction,
  variant = 'block',
  className,
  'data-testid': testId,
}: SectionErrorProps) {
  const inline = variant === 'inline';
  const compact = variant === 'compact';

  const retry = onRetry ? (
    <Button
      // A section can sit inside a <form> (Stock Sync settings): Retry must
      // never submit it.
      type="button"
      variant="outline"
      size="sm"
      onClick={onRetry}
      disabled={retrying}
      className={cn('gap-1.5', inline || compact ? 'h-8 text-xs' : undefined)}
    >
      {retrying ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {retryLabel}
    </Button>
  ) : null;
  const actions = secondaryAction ? (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {secondaryAction}
      {retry}
    </div>
  ) : (
    retry
  );

  if (inline) {
    return (
      <div
        role="alert"
        data-testid={testId}
        className={cn(
          'flex flex-wrap items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3',
          className,
        )}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-0.5">
          {title ? <p className="text-sm font-semibold">{title}</p> : null}
          {body ? <p className="text-xs leading-4 text-muted-foreground">{body}</p> : null}
        </div>
        {actions}
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 text-center',
        compact ? 'px-4 py-6' : 'px-4 py-14',
        className,
      )}
    >
      <AlertTriangle className={cn('text-destructive', compact ? 'h-6 w-6' : 'h-8 w-8')} aria-hidden="true" />
      {title || body ? (
        <div className="space-y-1">
          {title ? <p className="text-sm font-semibold">{title}</p> : null}
          {body ? <p className="mx-auto max-w-sm text-sm text-muted-foreground">{body}</p> : null}
        </div>
      ) : null}
      {actions}
    </div>
  );
}
